import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, Outlet, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import { useOperations } from "@/contexts/OperationsContext";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Badge } from "@/components/ui/badge";
import {
  AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle,
  AlertDialogDescription, AlertDialogFooter, AlertDialogAction, AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import NotificationsPopover from "@/components/NotificationsPopover";
// VoiceAssistant zastapiony przez ReportIssueButton (2026-04-15 — nikt nie uzywal voice)
import ReportIssueButton from "@/components/ReportIssueButton";
import AIProgressBar from "@/components/AIProgressBar";
import OnboardingWizard from "@/components/OnboardingWizard";
import MecenasChat from "@/components/MecenasChat";
import PWAInstallPrompt from "@/components/PWAInstallPrompt";
import { createWebSocket } from "@/lib/api";
import { roleLabel } from "@/lib/roleLabel";
import {
  LayoutDashboard, FileText, MessageSquare, FolderOpen, History,
  Scale, Menu, ChevronLeft, ChevronDown, LogOut, FileStack, User, Settings, Crown,
  Search, Database, BarChart3, Command, TrendingUp, Bot, Calculator, Shield, Building2, Handshake, Share2, Trash2, Mail, Loader2, Lightbulb, Bug, FileSearch, Sparkles, LayoutGrid,
} from "lucide-react";

const sidebarItems = [
  { labelKey: "nav.dashboard", icon: LayoutDashboard, path: "/dashboard" },
  // Grouped sidebar — 4 categories for lawyers (not developers)
];

const sidebarGroups = [
  {
    label: "nav.group_daily",    // "Codzienna praca"
    items: [
      { labelKey: "nav.dashboard", icon: LayoutDashboard, path: "/dashboard" },
      { labelKey: "nav.advice", icon: Lightbulb, path: "/advice" },
      { labelKey: "nav.contract_analysis", icon: Shield, path: "/contract-analysis" },
      { labelKey: "nav.negotiation", icon: Handshake, path: "/negotiation" },
      { labelKey: "nav.global_history", icon: History, path: "/history" },
    ],
  },
  {
    label: "nav.group_tools",    // "Narzedzia"
    items: [
      { labelKey: "nav.legal_search", icon: Search, path: "/legal-search" },
      { labelKey: "nav.prediction", icon: TrendingUp, path: "/prediction" },
      { labelKey: "nav.templates", icon: FolderOpen, path: "/templates" },
      { labelKey: "nav.calculators", icon: Calculator, path: "/calculators" },
      { labelKey: "nav.court_portal", icon: Building2, path: "/court-portal" },
      { labelKey: "nav.knowledge_base", icon: Database, path: "/knowledge-base" },
    ],
  },
  {
    label: "nav.group_advanced", // "Zaawansowane"
    items: [
      // 2026-05-03 INTEGRATION A: stara /detective scalona w /mecenas-detektyw
      // (CEIDG + KRS deep + Altman Z dodane jako tab "Dane firmy + Finanse").
      // Stara strona dostepna pod ukrytym /detective dla legacy bookmarks.
      // 2026-05-06: adminOnly=true bo Wywiad AI ma agressive OSINT (deep web scrape,
      // multi-provider intel). Tylko owner+admin mają audit accountability.
      { labelKey: "nav.mecenas_detektyw", icon: Sparkles, path: "/mecenas-detektyw", adminOnly: true, plans: ["kancelaria", "pro", "enterprise", "kancelaria_pro"] },
      { labelKey: "nav.due_diligence", icon: Building2, path: "/due-diligence" },
      { labelKey: "nav.compliance", icon: Shield, path: "/compliance" },
      { labelKey: "nav.benchmark", icon: BarChart3, path: "/benchmark" },
      { labelKey: "nav.analytics", icon: TrendingUp, path: "/analytics" },
      { labelKey: "nav.knowledge_graph", icon: Share2, path: "/knowledge-graph" },
    ],
  },
  {
    label: "nav.group_manage",   // "Zarzadzanie"
    items: [
      { labelKey: "nav.inbox", icon: Mail, path: "/inbox" },
      { labelKey: "nav.my_reports", icon: Bug, path: "/reports/my" },
      { labelKey: "nav.trash", icon: Trash2, path: "/trash" },
      { labelKey: "nav.settings", icon: Settings, path: "/settings" },
      { labelKey: "nav.admin_inbox", icon: Mail, path: "/admin/inbox", ownerOnly: true },
      { labelKey: "nav.admin", icon: Crown, path: "/admin", adminOnly: true },
      { labelKey: "nav.admin_reports", icon: Bug, path: "/admin/reports", adminOnly: true },
      { labelKey: "nav.api", icon: Command, path: "/developer", adminOnly: true },
    ],
  },
];

const projectItems = [
  { labelKey: "nav.creator", icon: FileText, path: "creator" },
  { labelKey: "nav.studio", icon: MessageSquare, path: "studio" },
  { labelKey: "nav.sources", icon: FolderOpen, path: "sources" },
  { labelKey: "nav.history", icon: History, path: "history" },
  { labelKey: "nav.pdf_lab", icon: FileStack, path: "pdf-lab" },
  { labelKey: "nav.agent_ai", icon: Bot, path: "agent" },
];

function SidebarContent({ collapsed, projectId, currentPath, onNavigate }) {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const isProjectView = projectId != null;

  // 2026-06-10 (UI-1): zwijane grupy nawigacji — domyslnie otwarta tylko
  // "Codzienna praca", reszta zwinieta (mniej chaosu). Stan w localStorage.
  const NAV_GROUPS_KEY = "leslex.nav.openGroups";
  const [openGroups, setOpenGroups] = useState(() => {
    try {
      const saved = JSON.parse(localStorage.getItem(NAV_GROUPS_KEY) || "null");
      if (saved && typeof saved === "object") return saved;
    } catch (_) {}
    return { "nav.group_daily": true };
  });
  const toggleGroup = (label) => {
    setOpenGroups((prev) => {
      const next = { ...prev, [label]: !prev[label] };
      try { localStorage.setItem(NAV_GROUPS_KEY, JSON.stringify(next)); } catch (_) {}
      return next;
    });
  };
  // Auto-rozwin grupe aktywnej strony (biezaca pozycja zawsze widoczna)
  useEffect(() => {
    const activeGroup = sidebarGroups.find((g) =>
      g.items.some((it) => currentPath === it.path || currentPath.startsWith(it.path + "/"))
    );
    if (activeGroup) {
      setOpenGroups((prev) => (prev[activeGroup.label] ? prev : { ...prev, [activeGroup.label]: true }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPath]);

  const handleClick = (e, path) => {
    if (onNavigate) {
      e.preventDefault();
      onNavigate(path);
    }
  };

  return (
    <div className="flex flex-col h-full bg-primary text-primary-foreground">
      {/* Logo */}
      <div className="p-4 flex items-center gap-2">
        <Scale className="h-6 w-6 text-gold shrink-0" />
        {!collapsed && <span className="font-heading text-lg font-bold text-white">LES-LEX</span>}
      </div>
      <Separator className="bg-white/10" />

      {/* Navigation — grouped sidebar */}
      <ScrollArea className="flex-1 py-2">
        <nav className="space-y-1 px-2">
          {sidebarGroups.map((group) => {
            const visibleItems = group.items.filter((item) => {
              if (item.ownerOnly) return user?.role === "owner";
              if (item.adminOnly) return ["owner", "admin"].includes(user?.role);
              if (item.plans) {
                // Visible for listed plans OR owner/admin (always see everything)
                if (["owner", "admin"].includes(user?.role)) return true;
                return item.plans.includes(user?.plan);
              }
              return true;
            });
            if (visibleItems.length === 0) return null;
            const isOpen = collapsed || !!openGroups[group.label];
            return (
              <div key={group.label} className="mb-1">
                {!collapsed ? (
                  <button
                    type="button"
                    onClick={() => toggleGroup(group.label)}
                    aria-expanded={!!openGroups[group.label]}
                    className="w-full flex items-center justify-between px-3 pt-3 pb-1 text-[10px] font-semibold text-white/30 uppercase tracking-widest hover:text-white/60 transition-colors"
                  >
                    <span>{t(group.label, group.label.split(".").pop())}</span>
                    <ChevronDown className={`h-3 w-3 shrink-0 transition-transform duration-200 ${openGroups[group.label] ? "" : "-rotate-90"}`} />
                  </button>
                ) : (
                  <Separator className="my-2 bg-white/10" />
                )}
                {isOpen && visibleItems.map((item) => {
                  const active = currentPath === item.path || currentPath.startsWith(item.path + "/");
                  return (
                    <Link key={item.path} to={item.path} onClick={(e) => handleClick(e, item.path)}>
                      <div className={`flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                        active
                          ? "bg-gold/20 text-gold"
                          : "text-white/70 hover:bg-white/10 hover:text-white"
                      }`}>
                        <item.icon className={`h-4 w-4 shrink-0 ${active ? "text-gold" : ""}`} />
                        {!collapsed && <span className="truncate" title={t(item.labelKey)}>{t(item.labelKey)}</span>}
                      </div>
                    </Link>
                  );
                })}
              </div>
            );
          })}

          {isProjectView && (
            <>
              <Separator className="my-3 bg-white/10" />
              <p className={`px-3 mb-2 text-xs font-medium text-white/40 uppercase tracking-wider ${collapsed ? "hidden" : ""}`}>
                {t("nav.project")}
              </p>
              {projectItems.map((item) => {
                const fullPath = `/project/${projectId}/${item.path}`;
                const active = currentPath === fullPath;
                return (
                  <Link key={item.path} to={fullPath} onClick={(e) => handleClick(e, fullPath)}>
                    <div className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                      active
                        ? "bg-gold/20 text-gold"
                        : "text-white/70 hover:bg-white/10 hover:text-white"
                    }`} data-testid={`project-tab-${item.path}`}>
                      <item.icon className={`h-4 w-4 shrink-0 ${active ? "text-gold" : ""}`} />
                      {!collapsed && <span className="truncate" title={t(item.labelKey)}>{t(item.labelKey)}</span>}
                    </div>
                  </Link>
                );
              })}
            </>
          )}
        </nav>

        {/* Search hint */}
        {!collapsed && (
          <div className="px-4 mt-4">
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white/5 text-white/40 text-xs border border-white/10 overflow-hidden">
              <Command className="h-3 w-3 shrink-0" />
              <span className="truncate" title={t("nav.search_hint")}>{t("nav.search_hint")}</span>
            </div>
          </div>
        )}
      </ScrollArea>

      {/* User info + logout */}
      <Separator className="bg-white/10" />
      <div className="p-2 space-y-1">
        {user && !collapsed && (
          <div className="px-3 py-2 text-xs text-white/50 space-y-1">
            <div className="flex items-center gap-2">
              <User className="h-3.5 w-3.5" />
              <span className="truncate text-white/70">{user.name || user.email}</span>
            </div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {user.plan && user.plan !== "free" && (
                <div className="flex items-center gap-1 text-gold">
                  <Crown className="h-3 w-3" />
                  <span className="text-[10px] font-medium uppercase">{user.plan}</span>
                </div>
              )}
              {user.role && (
                <Badge className="text-[9px] px-1.5 py-0 bg-white/10 text-white/70 border-white/20">
                  {roleLabel(user, t)}
                </Badge>
              )}
            </div>
          </div>
        )}
        <button onClick={logout} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm text-white/60 hover:bg-white/10 hover:text-white transition-all duration-200">
          <LogOut className="h-4 w-4 shrink-0" />
          {!collapsed && <span>{t("actions.logout")}</span>}
        </button>
      </div>
    </div>
  );
}

export default function AppShell() {
  const [collapsed, setCollapsed] = useState(false);
  const [wsToast, setWsToast] = useState(null);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const wsRef = useRef(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const projectMatch = location.pathname.match(/\/project\/([^/]+)/);
  const projectId = projectMatch ? projectMatch[1] : null;

  // Global operations tracking + navigation guard
  const { hasRunning, runningList } = useOperations();

  // Browser refresh/close protection
  useEffect(() => {
    if (!hasRunning) return;
    const handler = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [hasRunning]);

  // Navigation handler — intercepts sidebar clicks when operations are running
  const handleNavigate = useCallback((path) => {
    if (hasRunning) {
      setPendingNavigation(path);
    } else {
      navigate(path);
    }
  }, [hasRunning, navigate]);

  // WebSocket for real-time notifications
  const handleWsMessage = useCallback((event, data) => {
    // krok 2 (02.09): komponenty nasłuchują na `window` ("leslex:ws"), nie na instancji
    // window.__leslex_ws — ta jest podmieniana przy reconnect (api.js) i powstaje PO efektach
    // dzieci (Dashboard po zalogowaniu miał ws===undefined -> brak listenera)
    try {
      window.dispatchEvent(new CustomEvent("leslex:ws", { detail: { event, data } }));
    } catch {
      // brak CustomEvent (stare WebView) — reszta handlera działa jak dotąd
    }
    if (event === "notification" || event === "document_ready" || event === "deadline_alert") {
      setWsToast({ title: data.title || "Powiadomienie", message: data.message || "" });
      setTimeout(() => setWsToast(null), 5000);
    } else if (event === "report_updated") {
      // 2026-04-29 user-facing reports loop. Admin updated user's report —
      // toast + link do MyReportsPage. Krytyczny dla statusu awaiting_user_confirmation
      // (user MUSI zareagować w 3 dni inaczej auto-resolve).
      const isAwaiting = data.status === "awaiting_user_confirmation";
      const title = isAwaiting
        ? "✋ Twoje zgłoszenie czeka na potwierdzenie"
        : "📩 Aktualizacja Twojego zgłoszenia";
      const msgParts = [];
      if (data.title) msgParts.push(`„${data.title.length > 60 ? data.title.slice(0, 60) + "…" : data.title}"`);
      if (isAwaiting) {
        msgParts.push("Sprawdź czy fix zadziałał i potwierdź lub odrzuć.");
      } else if (data.has_new_response) {
        msgParts.push("Zespół dodał odpowiedź.");
      } else {
        msgParts.push(`Status: ${data.status}`);
      }
      setWsToast({
        title,
        message: msgParts.join(" — "),
        actionHref: `/reports/my?id=${data.report_id}`,
        actionLabel: "Otwórz",
      });
      // Awaiting confirmation = ważne, dłuższy timeout
      setTimeout(() => setWsToast(null), isAwaiting ? 12000 : 6000);
    }
  }, []);

  useEffect(() => {
    const token = localStorage.getItem("les_lex_token");
    if (token && !wsRef.current) {
      wsRef.current = createWebSocket(handleWsMessage);
      // Expose WS for AIProgressBar and other components
      window.__leslex_ws = wsRef.current;
    }
    return () => {
      if (wsRef.current) {
        if (wsRef.current._cleanup) {
          wsRef.current._cleanup();
        } else {
          wsRef.current.close();
        }
        wsRef.current = null;
      }
    };
  }, [handleWsMessage]);

  return (
    <div className="min-h-screen flex">
      {/* Desktop sidebar */}
      <aside className={`hidden md:flex flex-col transition-all duration-200 ${collapsed ? "w-16" : "w-60"}`}>
        <SidebarContent collapsed={collapsed} projectId={projectId} currentPath={location.pathname} onNavigate={handleNavigate} />
        <div className="bg-primary">
          <button onClick={() => setCollapsed(!collapsed)} className="p-2 mx-2 mb-2 rounded-lg text-white/40 hover:bg-white/10 hover:text-white transition-all duration-200">
            <ChevronLeft className={`h-4 w-4 transition-transform ${collapsed ? "rotate-180" : ""}`} />
          </button>
        </div>
      </aside>

      {/* Mobile menu */}
      <Sheet>
        <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-primary px-4 py-3 flex items-center gap-3">
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon" className="text-white/80 hover:bg-white/10 hover:text-white">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <Scale className="h-5 w-5 text-gold" />
          <span className="font-heading font-bold text-white">LES-LEX</span>
          <div className="ml-auto">
            <NotificationsPopover />
          </div>
        </div>
        <SheetContent side="left" className="w-60 p-0 border-r-0">
          <SidebarContent collapsed={false} projectId={projectId} currentPath={location.pathname} onNavigate={handleNavigate} />
        </SheetContent>
      </Sheet>

      {/* Main content */}
      <main className="flex-1 min-h-screen md:pt-0 pt-14">
        {/* Top bar — glassmorphic */}
        <div className="hidden md:flex items-center justify-end px-6 py-2 border-b bg-card/80 backdrop-blur-md">
          {hasRunning && (
            <div className="flex items-center gap-1.5 text-xs text-amber-600 bg-amber-50 dark:bg-amber-900/20 dark:text-amber-400 px-2.5 py-1 rounded-full mr-2 animate-pulse">
              <Loader2 className="h-3 w-3 animate-spin" />
              <span>{runningList.length} {runningList.length === 1 ? "operacja" : "operacje"}</span>
            </div>
          )}
          {/* Cockpit (beta) — dyskretne wejście w alternatywny interfejs. Additive,
              escape hatch wraca do klasyka. Nie zmienia istniejących elementów. */}
          <button
            type="button"
            onClick={() => navigate("/cockpit")}
            className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground border border-border rounded-full px-2.5 py-1 mr-2 transition-colors"
            title="Cockpit (beta) — alternatywny interfejs"
          >
            <LayoutGrid className="h-3.5 w-3.5" />
            <span>Cockpit</span>
            <span className="text-[9px] font-semibold uppercase tracking-wide text-gold">beta</span>
          </button>
          <NotificationsPopover />
        </div>
        <Outlet />
      </main>

      {/* Onboarding wizard — shows once for new users */}
      <OnboardingWizard />
      {/* AI Progress Bar (global — shows when AI pipeline is working) */}
      <AIProgressBar />
      {/* Mecenas LES-LEX — AI Help Chat (floating graduation cap button, left side) */}
      <MecenasChat />
      {/* Report Issue floating button */}
      <ReportIssueButton />
      {/* PWA Install Prompt + Offline indicator */}
      <PWAInstallPrompt />

      {/* WebSocket toast notification */}
      {wsToast && (
        <div className="fixed bottom-4 right-24 z-50 animate-in slide-in-from-bottom-4 bg-card border border-border rounded-lg shadow-card p-4 max-w-sm">
          <p className="text-sm font-semibold">{wsToast.title}</p>
          {wsToast.message && <p className="text-xs text-muted-foreground mt-1">{wsToast.message}</p>}
          {wsToast.actionHref && (
            <button
              type="button"
              onClick={() => { setWsToast(null); navigate(wsToast.actionHref); }}
              className="mt-2 text-xs font-medium text-primary hover:text-primary/80 underline"
            >
              {wsToast.actionLabel || "Otwórz"}
            </button>
          )}
        </div>
      )}

      {/* Navigation guard — shown when user tries to navigate with running operations */}
      <AlertDialog open={pendingNavigation !== null} onOpenChange={(open) => { if (!open) setPendingNavigation(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t("confirm_dialog.operation_in_progress")}</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div>
                <ul className="mt-2 list-disc pl-4 space-y-1">
                  {runningList.map((op, i) => <li key={i}>{op.label}</li>)}
                </ul>
                <p className="mt-3">{t("confirm_dialog.unsaved_changes")}</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setPendingNavigation(null)}>{t("actions.cancel")}</AlertDialogCancel>
            <AlertDialogAction onClick={() => {
              const target = pendingNavigation;
              setPendingNavigation(null);
              navigate(target);
            }}>{t("actions.confirm")}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
